import { ref, computed } from 'vue'
import { apiClient } from '@/services/auth-header'

export function useViewCounts() {
  const viewCounts = ref([])
  const selectedContentType = ref('ARTIST')
  const isLoading = ref(false)
  const error = ref(null)
  const pagination = ref({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    hasNext: false,
    hasPrevious: false
  })
  const sortBy = ref('viewCounts')
  const sortDirection = ref('desc')

  // 통계 계산
  const totalViewCounts = computed(() => pagination.value.totalElements)
  
  const averageViewCount = computed(() => {
    if (viewCounts.value.length === 0) return 0
    const total = viewCounts.value.reduce((sum, item) => sum + (item.viewCount || 0), 0)
    return Math.round(total / viewCounts.value.length)
  })

  // API 호출
  const fetchViewCounts = async () => {
    if (!selectedContentType.value) return
    
    isLoading.value = true
    error.value = null
    try {
      const params = {
        contentType: selectedContentType.value,
        page: pagination.value.page,
        size: pagination.value.size,
        sort: `${sortBy.value},${sortDirection.value}`
      }
      
      const response = await apiClient.get('/view-logs/view-count', { params })
      const data = response.data
      
      viewCounts.value = data.content || []
      pagination.value = {
        page: data.number ?? 0,
        size: data.size ?? 20,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
        first: data.first ?? true,
        last: data.last ?? true,
        hasNext: data.hasNext ?? !data.last ?? false,
        hasPrevious: data.hasPrevious ?? !data.first ?? false
      }
    } catch (err) {
      console.error('조회수 통계 데이터를 가져오는데 실패했습니다:', err)
      error.value = err.response?.data?.message || '데이터를 불러오는데 실패했습니다.'
      viewCounts.value = []
      pagination.value = {
        page: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true,
        hasNext: false,
        hasPrevious: false
      }
    } finally {
      isLoading.value = false
    }
  }

  // 새로고침
  const refreshData = () => {
    fetchViewCounts()
  }

  // 페이지 변경
  const changePage = (newPage) => {
    if (newPage >= 0 && newPage < pagination.value.totalPages) {
      pagination.value.page = newPage
      fetchViewCounts()
    }
  }

  // 페이지 크기 변경
  const changePageSize = (newSize) => {
    pagination.value.size = newSize
    pagination.value.page = 0 // 첫 페이지로 리셋
    fetchViewCounts()
  }

  // 정렬 변경
  const changeSort = (newSortBy, newSortDirection = 'desc') => {
    sortBy.value = newSortBy
    sortDirection.value = newSortDirection
    pagination.value.page = 0 // 첫 페이지로 리셋
    fetchViewCounts()
  }

  // 컨텐츠 타입 변경
  const changeContentType = (newContentType) => {
    selectedContentType.value = newContentType
    pagination.value.page = 0 // 첫 페이지로 리셋
    fetchViewCounts()
  }

  // 첫 페이지로 이동
  const goToFirstPage = () => changePage(0)

  // 마지막 페이지로 이동
  const goToLastPage = () => changePage(pagination.value.totalPages - 1)

  // 이전 페이지로 이동
  const goToPreviousPage = () => {
    if (pagination.value.hasPrevious) {
      changePage(pagination.value.page - 1)
    }
  }

  // 다음 페이지로 이동
  const goToNextPage = () => {
    if (pagination.value.hasNext) {
      changePage(pagination.value.page + 1)
    }
  }

  return {
    // 상태
    viewCounts,
    selectedContentType,
    isLoading,
    error,
    pagination,
    sortBy,
    sortDirection,
    
    // 계산된 값
    totalViewCounts,
    averageViewCount,
    
    // 메서드
    fetchViewCounts,
    refreshData,
    changePage,
    changePageSize,
    changeSort,
    changeContentType,
    goToFirstPage,
    goToLastPage,
    goToPreviousPage,
    goToNextPage
  }
}